import type { MultiPolygon, Polygon } from 'geojson';
import type { AreaOfResponsibility } from 'types/aor';
import type { GeoLocation } from 'components/GeoLocationComponent/GeoLocationComponent';
import type { AORHierarchyPayload } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/schema';
import { DISTRICT } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/schema';

type RrtDef = {
  id: string;
  name: string;
  description: string;
  /** [west, south, east, north] */
  bbox: [number, number, number, number];
};

/** Official NRS set: 10 regional RRTs plus Alaska, Caribbean, and Oceania. */
const RRT_DEFS: RrtDef[] = [
  {
    id: '00000000-0000-4000-a000-0000000000e1',
    name: 'RRT I — New England',
    description: 'EPA Regional Response Team 1 (New England)',
    bbox: [-73.7, 41.0, -66.9, 47.5],
  },
  {
    id: '00000000-0000-4000-a000-0000000000e2',
    name: 'RRT II — New York / New Jersey',
    description: 'EPA Regional Response Team 2 (New York / New Jersey)',
    bbox: [-80.0, 38.8, -71.8, 45.0],
  },
  {
    id: '00000000-0000-4000-a000-0000000000e3',
    name: 'RRT III — Mid-Atlantic',
    description: 'EPA Regional Response Team 3 (Mid-Atlantic)',
    bbox: [-83.7, 36.5, -74.7, 42.5],
  },
  {
    id: '00000000-0000-4000-a000-0000000000e4',
    name: 'RRT IV — Southeast',
    description: 'EPA Regional Response Team 4 (Southeast)',
    bbox: [-91.7, 24.4, -75.4, 37.0],
  },
  {
    id: '00000000-0000-4000-a000-0000000000e5',
    name: 'RRT V — Great Lakes',
    description: 'EPA Regional Response Team 5 (Great Lakes)',
    bbox: [-97.2, 36.9, -80.5, 49.4],
  },
  {
    id: '00000000-0000-4000-a000-0000000000e6',
    name: 'RRT VI — South Central',
    description: 'EPA Regional Response Team 6 (South Central)',
    bbox: [-109.1, 25.8, -88.8, 37.0],
  },
  {
    id: '00000000-0000-4000-a000-0000000000e7',
    name: 'RRT VII — Midwest',
    description: 'EPA Regional Response Team 7 (Midwest)',
    bbox: [-104.1, 36.0, -89.1, 43.5],
  },
  {
    id: '00000000-0000-4000-a000-0000000000e8',
    name: 'RRT VIII — Mountains and Plains',
    description: 'EPA Regional Response Team 8 (Mountains and Plains)',
    bbox: [-116.1, 37.0, -96.4, 49.0],
  },
  {
    id: '00000000-0000-4000-a000-0000000000e9',
    name: 'RRT IX — Pacific Southwest',
    description: 'EPA Regional Response Team 9 (Pacific Southwest)',
    bbox: [-124.6, 31.3, -109.0, 42.2],
  },
  {
    id: '00000000-0000-4000-a000-0000000000ea',
    name: 'RRT X — Pacific Northwest',
    description: 'EPA Regional Response Team 10 (Pacific Northwest)',
    bbox: [-125.0, 41.9, -111.0, 49.1],
  },
  {
    id: '00000000-0000-4000-a000-0000000000eb',
    name: 'Alaska RRT',
    description: 'EPA Regional Response Team — Alaska',
    bbox: [-172.0, 51.2, -129.9, 71.4],
  },
  {
    id: '00000000-0000-4000-a000-0000000000ec',
    name: 'Caribbean RRT',
    description: 'EPA Regional Response Team — Caribbean',
    bbox: [-68.2, 17.6, -64.2, 18.6],
  },
  {
    id: '00000000-0000-4000-a000-0000000000ed',
    name: 'Oceania RRT',
    description: 'EPA Regional Response Team — Oceania (Pacific Islands)',
    bbox: [-160.4, 18.8, -154.7, 22.4],
  },
];

/** Closed rectangle ring from a [west, south, east, north] bounding box. */
export function bboxPolygon(bbox: [number, number, number, number]) {
  const [west, south, east, north] = bbox;
  return {
    type: 'Polygon' as const,
    coordinates: [
      [
        [west, south],
        [east, south],
        [east, north],
        [west, north],
        [west, south],
      ],
    ],
  };
}

export const FEMA_REGION_META = [
  { number: 1, roman: 'I', id: '00000000-0000-4000-a000-0000000000e1' },
  { number: 2, roman: 'II', id: '00000000-0000-4000-a000-0000000000e2' },
  { number: 3, roman: 'III', id: '00000000-0000-4000-a000-0000000000e3' },
  { number: 4, roman: 'IV', id: '00000000-0000-4000-a000-0000000000e4' },
  { number: 5, roman: 'V', id: '00000000-0000-4000-a000-0000000000e5' },
  { number: 6, roman: 'VI', id: '00000000-0000-4000-a000-0000000000e6' },
  { number: 7, roman: 'VII', id: '00000000-0000-4000-a000-0000000000e7' },
  { number: 8, roman: 'VIII', id: '00000000-0000-4000-a000-0000000000e8' },
  { number: 9, roman: 'IX', id: '00000000-0000-4000-a000-0000000000e9' },
  { number: 10, roman: 'X', id: '00000000-0000-4000-a000-0000000000ea' },
] as const;

export type DemoGeom = Polygon | MultiPolygon;

function geomCentroid(geom: DemoGeom): [number, number] {
  const points: Array<[number, number]> = [];
  const walk = (value: unknown) => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number') {
      points.push([Number(value[0]), Number(value[1])]);
      return;
    }
    value.forEach(walk);
  };
  walk(geom.coordinates);
  if (points.length === 0) return [-96, 38];
  const lon = points.reduce((sum, point) => sum + point[0], 0) / points.length;
  const lat = points.reduce((sum, point) => sum + point[1], 0) / points.length;
  return [lon, lat];
}

/** GeoLocation payload (centroid + feature collection) for a demo polygon. */
export function locationPayload(name: string, geom: DemoGeom): GeoLocation {
  const [lon, lat] = geomCentroid(geom);
  return {
    id: name,
    address: name,
    name,
    centroid: {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
      properties: {},
    },
    geojson: {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: geom, properties: {} }],
      },
    },
  };
}

export function buildEpaDemoAor({
  groupGuid,
  id,
  name,
  description,
  geom,
}: {
  groupGuid: string;
  id: string;
  name: string;
  description: string;
  geom: DemoGeom;
}): AreaOfResponsibility {
  const location = locationPayload(name, geom);
  return {
    id: id as UUID,
    item_id: id as UUID,
    group_guid: groupGuid as UUID,
    name,
    description,
    location: JSON.stringify(location),
    current_location_geom: JSON.stringify(geom),
    category_id: null,
    parent_item_id: null,
    geolocation: location,
    cc_type: null,
    level_name: DISTRICT,
    color: null,
  };
}

export function getEpaRrtAors(groupGuid: string): AreaOfResponsibility[] {
  return RRT_DEFS.map((def) =>
    buildEpaDemoAor({
      groupGuid,
      id: def.id,
      name: def.name,
      description: def.description,
      geom: bboxPolygon(def.bbox),
    })
  );
}

/** 10 FEMA-named bbox rectangles used only if the live FEMA service fails. */
export function getEpaRrtAorsFallback(
  groupGuid: string
): AreaOfResponsibility[] {
  return FEMA_REGION_META.map((meta) => {
    const def = RRT_DEFS[meta.number - 1];
    return buildEpaDemoAor({
      groupGuid,
      id: meta.id,
      name: `FEMA Region ${meta.roman}`,
      description: `FEMA Region ${meta.roman}`,
      geom: bboxPolygon(def.bbox),
    });
  });
}

export function aorsToHierarchy(
  aors: AreaOfResponsibility[]
): AORHierarchyPayload {
  return aors.map((aor) => ({
    id: String(aor.id),
    name: aor.name,
    description: aor.description,
    parentItemId: null,
    location: aor.location ?? null,
    currentLocationGeom: aor.current_location_geom ?? null,
    levelName: DISTRICT,
    levelOrder: 1,
  }));
}

export function getEpaRrtAorsByHierarchy(
  groupGuid: string
): AORHierarchyPayload {
  return aorsToHierarchy(getEpaRrtAors(groupGuid));
}
